package pl.nickers.match3.ecs

import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonClassDiscriminator
import pl.nickers.match3.GRID_SIZE

@OptIn(ExperimentalSerializationApi::class)
@Serializable
@JsonClassDiscriminator("kind")
sealed interface PersistableComponent : Component

@Serializable
@SerialName("grid_pos")
data class GridPositionComponent(var row: Int, var col: Int) : PersistableComponent {

    fun setTo(other: GridPositionComponent) {
        row = other.row
        col = other.col
    }
}

@Serializable
@SerialName("jelly_type")
data class JellyTypeComponent(val type: Int) : PersistableComponent

@Serializable
@SerialName("body_image")
data class BodyImageComponent(val image: String) : PersistableComponent

@Serializable
@SerialName("jelly_face")
data class JellyFaceComponent(val image: String) : PersistableComponent

@Serializable
@SerialName("bomb_face")
data class BombFaceComponent(val image: String) : PersistableComponent

@Serializable
@SerialName("draggable")
class DraggableComponent : PersistableComponent

/**
 * The beam emitted by a laser special tile. The direction for a given laser
 * entity is derived from its piece definition ([PieceParams.Laser]).
 */
@Serializable
enum class LaserDirection {
    HORIZONTAL,
    VERTICAL,
}

@Serializable
@SerialName("crystallized_amber")
data class IceCubeComponent(var life: Int = 3) : PersistableComponent

/**
 * E5 - marks an ingredient as cursed (covered in shadow). A cursed ingredient still
 * clears, but is barren: no points, no goal credit. In a MATCH a single cursed item
 * spoils the whole group - none of that line's items score or count (see
 * `MatchDetectionSystem`). In an AREA effect (bomb/laser/swirl) scoring is per-item:
 * clean items in the blast still count, cursed ones do not (see `scoreDeleteAndCheckGoals`).
 * The curse is removed when the ingredient is matched while standing on a Purified Rune
 * (see `cleanseCursedOnRunes`); special tiles are never cursed.
 */
@Serializable
@SerialName("cursed")
class CursedComponent : PersistableComponent

/**
 * E5 - Void Puddle terrain. A persistent board-floor tile: its own entity with
 * [GridPositionComponent] but no [JellyTypeComponent], so it coexists with whatever
 * piece occupies the cell (the same shape as [TeleportTargetMarkerComponent]). Any
 * regular ingredient that comes to REST on the cell is stamped with
 * [CursedComponent] by `CurseSystem`; pieces merely falling through are spared, and
 * the puddle itself is never consumed.
 */
@Serializable
@SerialName("void_puddle")
class VoidPuddleTerrainComponent : PersistableComponent

/**
 * E5/E6 - Purified Rune terrain. Same entity shape as [VoidPuddleTerrainComponent].
 * A cursed ingredient matched while standing on a rune is cleansed and counts toward
 * goals normally. (The rune score boost lands with E6.)
 */
@Serializable
@SerialName("purified_rune")
class PurifiedRuneComponent : PersistableComponent

@Serializable
@SerialName("board_state")
data class BoardStateComponent(
    var phase: GamePhase = GamePhase.INPUT,
    var score: Int = 0,
    val width: Int = GRID_SIZE,
    val height: Int = width,
    var won: Boolean = false,
    val holes: Set<Pair<Int, Int>> = emptySet(),
) : PersistableComponent {
    @kotlinx.serialization.Transient
    var emptyCellSelection: Pair<Int, Int>? = null

    @kotlinx.serialization.Transient
    var didWork: Boolean = false

    @kotlinx.serialization.Transient
    var currentSubphase: ResolveSubphase? = null

}

@Serializable
enum class ResolveSubphase {
    SWAP,
    BOMB,
    LASER,
    EFFECT,
    MATCH_DETECT,
    GHOST,
}

@Serializable
data class Goal(
    val pieceId: String,
    val targetQuantity: Int,
    var remainingQuantity: Int,
    var completed: Boolean = false,
)

@Serializable
@SerialName("goals")
data class GoalsComponent(
    val goals: MutableList<Goal> = mutableListOf(),
) : PersistableComponent

@Serializable
@SerialName("board_spawn_control")
data class BoardSpawnControlComponent(
    var disableNewJellySpawn: Boolean = false,
) : PersistableComponent

@Serializable
@SerialName("spawn_point")
data class SpawnPointComponent(
    val visible: Boolean = true,
    val nextJellyPreviewVisible: Boolean = false,
) : PersistableComponent

/**
 * Marker entity stamped on a destination cell that pulls a jelly from [sourceRow]/[sourceCol]
 * during the gravity teleport pass (between vertical and diagonal). The marker entity
 * has only [GridPositionComponent] (the destination) plus this — no [JellyTypeComponent] —
 * so it coexists with whatever jelly happens to occupy the cell.
 */
@Serializable
@SerialName("teleport_target")
data class TeleportTargetMarkerComponent(
    val sourceRow: Int,
    val sourceCol: Int,
) : PersistableComponent

@Serializable
@SerialName("background_image")
data class BackgroundImageComponent(
    /**
     * The board background image encoded as a Base64 string (standard Base64 alphabet,
     * no line-wrapping).  Any common raster format supported by the platform (PNG, JPEG,
     * etc.) is accepted.  The image is scaled to fill the grid area at render time.
     */
    val imageBase64: String,
) : PersistableComponent

@Serializable
enum class PowerUpType {
    BOMB,
    ROW_BOMB,
    TARGETED_REMOVE,
}

@Serializable
@SerialName("power_up_type")
data class PowerUpTypeComponent(val type: PowerUpType) : PersistableComponent

@Serializable
@SerialName("power_up_uses")
data class PowerUpUsesComponent(val uses: Int) : PersistableComponent

@Serializable
@SerialName("move_limit")
data class MoveLimitComponent(
    val totalMoves: Int,
    var remainingMoves: Int,
) : PersistableComponent

/**
 * The kinds of Familiar a player can bring into a level. Only [CONJURER] and
 * [BLACK_CAT] have implemented activation logic so far (see `FamiliarSystem`);
 * the remaining roadmap types are reserved for E11.
 */
@Serializable
enum class FamiliarType {
    CONJURER, PURIFIER, STRIKER, NURTURER, SPARK_CASTER, HATCHER, SHATTERER, BREWER, BLACK_CAT
}

@Serializable
@SerialName("familiar")
data class FamiliarComponent(
    var progress: Int = 0,
    val threshold: Int = 10,
    val type: FamiliarType = FamiliarType.BLACK_CAT,
    /**
     * The ingredient whose collection advances [progress]. `null` counts every goal
     * ingredient collected (the Black Cat behaviour); a specific piece id counts only
     * that ingredient (the Conjurer's trigger).
     */
    val triggerPieceId: String? = null,
) : PersistableComponent

/**
 * Identifies the data-driven piece definition this entity was created from.
 *
 * The id matches a key in the active piece set's `pieces` map
 * (e.g. "ember_seed", "bomb", "laser_horizontal", "crystallized_amber"); systems read
 * the entity's behaviour from this id via [PieceDefHelpers].
 */
@Serializable
@SerialName("piece_id")
data class PieceIdComponent(val pieceId: String) : PersistableComponent

/**
 * Records which piece set the current board was created from. Attached to
 * the board entity. Future loaders use this to look up piece definitions
 * for restoring entities; absent on pre-piece-set save games.
 */
@Serializable
@SerialName("piece_set_id")
data class PieceSetIdComponent(val pieceSetId: String) : PersistableComponent

/**
 * Step of the level-1 guided tutorial (F16.1, issue #110). [SWAP_HINT] highlights a
 * swap that produces a match; [POINT_AT_GOALS] calls out the goal/moves counters after
 * the first successful match.
 */
@Serializable
enum class TutorialStep { SWAP_HINT, POINT_AT_GOALS }

/**
 * Tracks the level-1 tutorial's progress. Attached to the board entity only while
 * playing journey level 1 — `GameViewModel` decides that at board setup, since only it
 * knows the level id; this component and `TutorialSystem` don't special-case level 1
 * themselves. Re-attached fresh (step reset to [TutorialStep.SWAP_HINT]) every time a
 * `GameViewModel` for level 1 is constructed, so the tutorial reappears on every replay,
 * not just a player's first-ever attempt.
 *
 * A [PersistableComponent] so a future journey-level resume feature would restore
 * tutorial progress along with the rest of the board — journey levels don't currently
 * reload snapshots (see `GameViewModel`'s `savedSnapshot` gate), so this isn't exercised
 * yet, but modelling it as ECS state now means it's ready when that lands.
 */
@Serializable
@SerialName("tutorial")
data class TutorialComponent(
    var step: TutorialStep = TutorialStep.SWAP_HINT,
    val startScore: Int = 0,
) : PersistableComponent

// ---- Transient components (not saved) ----

class SelectedComponent : Component

class AudioQueueComponent(val events: MutableSet<pl.nickers.match3.SoundEvent> = mutableSetOf()) : Component

data class SwappingComponent(
    val sourceRow: Int,
    val sourceCol: Int,
    val targetRow: Int,
    val targetCol: Int,
    val isReturning: Boolean = false,
) : Component {

    val targetPosition get() = GridPositionComponent(targetRow, targetCol)
    val sourcePosition get() = GridPositionComponent(sourceRow, sourceCol)
}

data class FallingComponent(
    val fromRow: Int,
    val toRow: Int,
    val fromCol: Int = -1,
    val toCol: Int = -1,
) : Component

/**
 * Transient: stamped by the gravity teleport pass on the entity that just teleported.
 * Drives the "fall through a door" animation in the UI: the jelly slides out of the
 * source cell's bottom edge while a copy emerges from the destination cell's top edge.
 * Drained by [FallResolveSystem] like [FallingComponent].
 */
data class TeleportingComponent(
    val sourceRow: Int,
    val sourceCol: Int,
    val destRow: Int,
    val destCol: Int,
) : Component

class MatchNeighbourComponent : Component

/**
 * Transient marker placed on an entity that was caught in an area-of-effect this frame —
 * a bomb blast, laser sweep, row-bomb power-up step, or targeted-remove power-up.
 * [IceDamageSystem] consumes this on ice cubes; non-ice carriers are wiped on INPUT entry
 * by [MatchNeighbourCleanupSystem].
 */
class HitByAreaEffectComponent : Component

class ExplodingComponent : Component

class FullBoardExplosionComponent : Component

class FusedBombComponent : Component

/**
 * Transient marker placed on a swirl entity at activation time.
 * [targetType] is the regular jelly type whose colour should be cleared from the
 * board when the swirl detonates in [EffectResolveSystem]. Set by:
 *  - [SwapResolveSystem] when a swirl is swapped with a regular jelly (target = partner type)
 *  - [InputSystem] when a swirl is double-clicked (target = most common colour on board)
 *  - [resolveAreaOfEffect] when a swirl is caught in a bomb's blast (target = most common colour)
 */
data class ActivatedSwirlComponent(val targetPieceId: String) : Component

/**
 * Transient marker placed on regular jellies targeted by a colourful swirl activation.
 * Drives the vacuum animation (fade → fly+shrink) before the entities are deleted.
 * [swirlEntityId] identifies the swirl to delete once animation completes.
 * [targetRow]/[targetCol] is the swirl's grid position (animation destination).
 */
data class SwirlVacuumComponent(
    val swirlEntityId: Int,
    val targetRow: Int,
    val targetCol: Int,
) : Component

/**
 * Transient: a cross bomb entity between its first and second explosion.
 * The entity has no [GridPositionComponent] in this state (invisible, unaffected by gravity).
 * [row] and [col] store the original explosion position for the second detonation.
 */
data class GhostBombComponent(val row: Int, val col: Int) : Component

/**
 * Transient activation state for a laser beam animation.
 *
 * [directions] lets special swaps override the beam set (e.g. swapping two lasers
 * produces a cross from the moved laser). [forcedConsumedEntities] are entities that
 * should be removed when this activation resolves even if they would normally chain.
 */
data class ActivatedLaserComponent(
    val directions: Set<LaserDirection>,
    val forcedConsumedEntities: Set<Int> = emptySet(),
) : Component

data class PendingSwapComponent(
    val fromRow: Int,
    val fromCol: Int,
    val toRow: Int,
    val toCol: Int,
    val isReturningSwap: Boolean = false,
) : Component

class ConsumedComponent : Component

class JustMovedComponent : Component

data class FlyingSpriteComponent(
    val row: Int,
    val col: Int,
    val bodyImage: String,
    val faceImage: String,
    val goalPieceId: String,
) : Component

/**
 * A sprite flying from the familiar display to a jelly the familiar just transmuted.
 * The board-direction mirror of [FlyingSpriteComponent] (which flies cell -> goal).
 */
data class FamiliarFlightComponent(
    val row: Int,
    val col: Int,
    val bodyImage: String,
    val faceImage: String,
) : Component

data class BombFlightComponent(
    val targetRow: Int,
    val targetCol: Int,
    val powerUpEntityId: Int,
) : Component

data class ScreenTransformComponent(
    var offsetX: Float = 0f,
    var offsetY: Float = 0f,
    var scale: Float = 1f,
    var alpha: Float = 1f,
) : Component

@Serializable
enum class GamePhase {
    INPUT,
    ANIMATE,
    GRAVITY,
    RESOLVE;

    val isAnimating: Boolean get() = this == ANIMATE
}
